<template>
  <Teleport to="body">
    <Transition :name="variant === 'drawer' ? 'drawer' : 'modal'">
      <div
        v-if="isOpen"
        class="dialog-overlay"
        :class="overlayClasses"
        @click="handleOverlayClick"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="headerId"
      >
        <div class="dialog-container" :class="containerClasses" @click.stop>
          <!-- Header -->
          <div class="dialog-header">
            <slot name="header">
              <h2 :id="headerId" class="dialog-title">{{ title }}</h2>
            </slot>
            <button
              @click="emit('close')"
              class="close-button"
              :aria-label="closeLabel || $t('common.close')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="dialog-content" :class="contentClasses">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div v-if="$slots.footer" class="dialog-footer" :class="footerClasses">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  isOpen: boolean
  title?: string
  closeLabel?: string
  variant?: 'modal' | 'drawer'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  preventOverlayClose?: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

// Generate unique ID for accessibility
const headerId = `dialog-header-${Math.random().toString(36).substr(2, 9)}`

const overlayClasses = computed(() => {
  const classes = ['dialog-overlay']

  if (props.variant === 'drawer') {
    classes.push('drawer-overlay')
  } else {
    classes.push('modal-overlay')
  }

  return classes
})

const containerClasses = computed(() => {
  const classes = ['dialog-container']

  if (props.variant === 'drawer') {
    classes.push('drawer-container')
  } else {
    classes.push('modal-container')

    // Add size classes for modal
    switch (props.size) {
      case 'sm':
        classes.push('modal-sm')
        break
      case 'lg':
        classes.push('modal-lg')
        break
      case 'xl':
        classes.push('modal-xl')
        break
      default:
        classes.push('modal-md')
    }
  }

  return classes
})

const contentClasses = computed(() => {
  const classes = ['dialog-content']

  if (props.variant === 'drawer') {
    classes.push('drawer-content')
  } else {
    classes.push('modal-content')
  }

  return classes
})

const footerClasses = computed(() => {
  const classes = ['dialog-footer']

  if (props.variant === 'drawer') {
    classes.push('drawer-footer')
  } else {
    classes.push('modal-footer')
  }

  return classes
})

const handleOverlayClick = () => {
  if (!props.preventOverlayClose) {
    emit('close')
  }
}

// Handle keyboard events
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isOpen) {
    emit('close')
  }
}

// Add/remove event listeners when dialog opens/closes
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
    }
  },
  { immediate: true }
)
</script>

<style scoped>
/* Base dialog styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-container {
  background: theme('colors.white');
  display: flex;
  flex-direction: column;

  .dark & {
    background: theme('colors.stone.900');
  }
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: theme('spacing.4');
}

.dialog-title {
  font-size: theme('fontSize.lg');
  font-weight: theme('fontWeight.semibold');
  color: theme('colors.stone.900');
  margin: 0;

  .dark & {
    color: theme('colors.stone.100');
  }
}

.close-button {
  padding: theme('spacing.2');
  border-radius: theme('borderRadius.md');
  color: theme('colors.stone.400');
  transition: all 0.2s ease;

  &:hover {
    color: theme('colors.stone.600');
    background: theme('colors.stone.100');
  }

  .dark & {
    color: theme('colors.stone.500');

    &:hover {
      color: theme('colors.stone.300');
      background: theme('colors.stone.800');
    }
  }
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
}

.dialog-footer {
  /* border-top: 1px solid theme('colors.stone.200');

  .dark & {
    border-top-color: theme('colors.stone.700');
  } */
}

/* Modal-specific styles */
.modal-overlay {
  padding: theme('spacing.4');

  @media (min-width: theme('screens.lg')) {
    z-index: 9999;
    margin-left: 300px; /* Account for sidebar width */
  }
}

.modal-container {
  border-radius: 16px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-sm {
  max-width: 400px;
}

.modal-md {
  max-width: 500px;
}

.modal-lg {
  max-width: 600px;
}

.modal-xl {
  max-width: 800px;
}

.modal-content {
  padding: theme('spacing.6');
}

.modal-footer {
  display: flex;
  gap: theme('spacing.3');
  padding: theme('spacing.4') theme('spacing.6') theme('spacing.6');
  justify-content: flex-end;
}

.modal-header .dialog-title {
  font-size: theme('fontSize.xl');
  padding: theme('spacing.2') 0;
}

/* Drawer-specific styles */
.drawer-overlay {
  align-items: flex-end;
  justify-content: center;

  @media (min-width: theme('screens.lg')) {
    align-items: center;
    z-index: 9999;
  }
}

.drawer-container {
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-height: calc(100dvh - 55px); /* Account for nav bar height */
  transform: translateY(0);
  margin-bottom: 55px; /* Space for nav bar */

  @media (min-width: theme('screens.lg')) {
    border-radius: 16px;
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    margin-bottom: 0;
    margin-left: 300px; /* Account for sidebar width */
    transform: none;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }
}

.drawer-content {
  padding: theme('spacing.4');
  min-height: 200px;
}

.drawer-footer {
  padding: theme('spacing.4');
  background: theme('colors.stone.50');

  .dark & {
    background: theme('colors.stone.800');
  }
}

/* Transition animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95);
}

.drawer-enter-active,
.drawer-leave-active {
  transition: all 0.3s ease-out;

  @media (min-width: theme('screens.lg')) {
    transition: all 0.15s ease-out;
  }
}

.drawer-enter-active .drawer-container,
.drawer-leave-active .drawer-container {
  transition: transform 0.3s ease-out;

  @media (min-width: theme('screens.lg')) {
    transition: transform 0.15s ease-out;
  }
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .drawer-container {
  transform: translateY(100%);
}

.drawer-leave-to .drawer-container {
  transform: translateY(100%);
}

@media (min-width: theme('screens.lg')) {
  .drawer-enter-from .drawer-container,
  .drawer-leave-to .drawer-container {
    transform: scale(0.95) translateY(20px);
  }
}
</style>
